/**
 * Click-level event taxonomy for pro (KB-2608).
 *
 * Every instrumented surface imports its event name from here — never a
 * string literal at the call site. A typo'd literal is undetectable in
 * GA4; it just becomes a new event. Naming standard:
 *
 *   <surface>_<object>_<verb>   e.g. org_edit_save, nav_offers_click
 *
 * lowercase snake_case, no free text. Scope (Nicholas's ruling,
 * 2026-08-24): auth, primary nav, and save/submit on organizations,
 * locations, offers, users. Not blanket instrumentation of every button.
 */
export const ANALYTICS_EVENTS = {
  // Auth — instrumented at the UI boundary (LoginPage.tsx, the logout
  // controls), never inside AuthContext.tsx. See KB-2608 brief.
  AUTH_LOGIN_SUBMIT: 'auth_login_submit',
  AUTH_LOGIN_SUCCESS: 'auth_login_success',
  AUTH_LOGIN_FAILURE: 'auth_login_failure',
  AUTH_LOGOUT_CLICK: 'auth_logout_click',

  // Primary nav — one per sidebar destination (src/components/ui/sidebar.tsx).
  NAV_DASHBOARD_CLICK: 'nav_dashboard_click',
  NAV_ORGANIZATIONS_CLICK: 'nav_organizations_click',
  NAV_LOCATIONS_CLICK: 'nav_locations_click',
  NAV_OFFERS_CLICK: 'nav_offers_click',
  NAV_USERS_CLICK: 'nav_users_click',
  NAV_ADMIN_REPORTS_CLICK: 'nav_admin_reports_click',
  NAV_GRAPHS_CLICK: 'nav_graphs_click',
  NAV_TREEMAP_CLICK: 'nav_treemap_click',
  NAV_ACCOUNT_REQUESTS_CLICK: 'nav_account_requests_click',
  NAV_FIELDS_OPTIONS_CLICK: 'nav_fields_options_click',
  NAV_ADMIN_TRAFFIC_RECONCILIATION_CLICK: 'nav_admin_traffic_reconciliation_click',
  NAV_CAMP_DATA_CLICK: 'nav_camp_data_click',
  NAV_ADMIN_MY_GROUP_VIEWER_CLICK: 'nav_admin_my_group_viewer_click',
  NAV_ADMIN_GROUP_SHEETS_CLICK: 'nav_admin_group_sheets_click',
  NAV_EMAIL_TEMPLATES_CLICK: 'nav_email_templates_click',
  NAV_SYSTEM_EMAILS_CLICK: 'nav_system_emails_click',
  NAV_COMMS_CLICK: 'nav_comms_click',
  NAV_OUTREACH_CLICK: 'nav_outreach_click',
  NAV_SETTINGS_CLICK: 'nav_settings_click',
  NAV_SUPPORT_CLICK: 'nav_support_click',
  NAV_SUPPORT_CREATE_CLICK: 'nav_support_create_click',
  NAV_DEBUG_USER_CLICK: 'nav_debug_user_click',
  NAV_ADMIN_METRICS_HEALTH_CLICK: 'nav_admin_metrics_health_click',
  NAV_ADMIN_CAMP_INGEST_LEDGER_CLICK: 'nav_admin_camp_ingest_ledger_click',

  // Page view — internal sink only, one per route change after login (KB-4771).
  // `page` carries the route PATTERN (`/organizations/:id/edit`), never the
  // pathname. GA4 keeps its own page_view (KB-2607), so this never goes there.
  ROUTE_PAGE_VIEW: 'route_page_view',

  // Core CRUD — save/submit on organizations, locations, offers, users.
  ORG_CREATE_SUBMIT: 'org_create_submit',
  ORG_EDIT_SAVE: 'org_edit_save',
  LOCATION_CREATE_SUBMIT: 'location_create_submit',
  LOCATION_EDIT_SAVE: 'location_edit_save',
  OFFER_CREATE_SUBMIT: 'offer_create_submit',
  OFFER_EDIT_SAVE: 'offer_edit_save',
  USER_EDIT_SAVE: 'user_edit_save',

  // The failed twin of each save above (KB-4771): emitted where the save itself
  // rejects, or resolves reporting nothing written (`reason: no-result`), so a
  // save emits exactly one of the pair. `reason` is the error's code, never its
  // message.
  ORG_CREATE_FAILURE: 'org_create_failure',
  ORG_EDIT_FAILURE: 'org_edit_failure',
  LOCATION_CREATE_FAILURE: 'location_create_failure',
  LOCATION_EDIT_FAILURE: 'location_edit_failure',
  OFFER_CREATE_FAILURE: 'offer_create_failure',
  OFFER_EDIT_FAILURE: 'offer_edit_failure',
  USER_EDIT_FAILURE: 'user_edit_failure',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];
